// Intent: realny sync gospodarstwa — two phones, one set of shared data. Unlike
// cloud-sync (a personal backup keyed by the account), this reconciles a SHARED
// blob keyed by the household, using ROW-LEVEL last-write-wins so both partners
// can edit concurrently without clobbering each other. Scope = budget/goals/
// income/fixed costs + household kv (see HOUSEHOLD_TABLES); policies stay
// private and the vault never leaves the device. Everything is best-effort and
// feature-detected — the current prod backend (1.52) lacks these endpoints, so
// a 404 simply keeps the feature off; local data always works.

import { getDb } from '@/db';
import {
  fetchHouseholdApi,
  pullHouseholdSyncApi,
  pushHouseholdSyncApi,
} from '@/api/mobile-api';
import { exportSnapshotOf, HOUSEHOLD_TABLES, importSnapshotMerge, type AppSnapshot } from '@/db/snapshot';
import { useAuthStore } from '@/store/auth-store';

const KV_HH_SYNC_TS = 'household_sync_client_updated_at';

// Cached membership: null = unknown/none. Re-checked lazily so a freshly-joined
// partner starts syncing without an app restart.
let householdId: number | null = null;
let membershipChecked = false;

/** Load membership once on boot (before the first syncHouseholdNow). */
export async function initHouseholdSync(): Promise<void> {
  await refreshMembership();
}

async function refreshMembership(): Promise<void> {
  membershipChecked = true;
  if (!useAuthStore.getState().account) {
    householdId = null;
    return;
  }
  const res = await fetchHouseholdApi();
  householdId = res.ok && res.data ? res.data.id : null;
}

export function householdSyncActive(): boolean {
  return householdId !== null;
}

/** Call after joining/creating a household so sync turns on immediately. */
export function setHouseholdActive(id: number | null): void {
  householdId = id;
  membershipChecked = true;
}

function parseSnapshot(raw: string): AppSnapshot | null {
  try {
    return JSON.parse(raw) as AppSnapshot;
  } catch {
    return null;
  }
}

/**
 * Reconcile the shared household blob once. No-op unless logged in AND a member
 * of a server household. pull -> merge into local -> push the merged subset.
 */
export async function syncHouseholdNow(): Promise<void> {
  if (!useAuthStore.getState().account) return;
  if (!membershipChecked || householdId === null) {
    await refreshMembership();
    if (householdId === null) return;
  }

  const db = await getDb();
  const localTs = Number((await db.getKv(KV_HH_SYNC_TS)) ?? '0') || 0;

  // 1. Pull + merge whatever the server has (row-level LWW into local).
  const pulled = await pullHouseholdSyncApi();
  if (!pulled.ok) {
    // 404/401 -> membership likely gone; drop the cache so we re-check next time.
    if (pulled.data === undefined) householdId = null;
    return;
  }
  const serverTs = pulled.data?.clientUpdatedAt ?? 0;
  if (pulled.data?.payload) {
    const snap = parseSnapshot(pulled.data.payload);
    if (snap) await importSnapshotMerge(snap, HOUSEHOLD_TABLES);
  }

  // 2. Push the merged local subset back with a fresh stamp.
  const stamp = Date.now();
  const merged = await exportSnapshotOf(HOUSEHOLD_TABLES);
  const res = await pushHouseholdSyncApi(JSON.stringify(merged), stamp);
  if (res.ok && res.data?.accepted) {
    await db.setKv(KV_HH_SYNC_TS, String(stamp));
  } else if (res.ok && res.data && !res.data.accepted && res.data.payload) {
    // Race: another phone pushed a newer stamp between our pull and push —
    // merge that too so nothing is lost.
    const snap = parseSnapshot(res.data.payload);
    if (snap) await importSnapshotMerge(snap, HOUSEHOLD_TABLES);
    await db.setKv(KV_HH_SYNC_TS, String(res.data.clientUpdatedAt ?? serverTs));
  }
}

// Debounced trigger for store mutations — collapses a burst of edits into one
// sync ~5 s later, and no-ops entirely when the feature is off.
let debounceHandle: ReturnType<typeof setTimeout> | null = null;
export function scheduleHouseholdSync(): void {
  if (householdId === null) return;
  if (debounceHandle) clearTimeout(debounceHandle);
  debounceHandle = setTimeout(() => {
    debounceHandle = null;
    void syncHouseholdNow().catch(() => undefined);
  }, 5000);
}
